import React from "react";
import FieldLabel from "@comps/inputs/BaseFieldLabel";
import { commonFieldTooltips } from "./commonFieldTooltips";

// Keys already reported in development, so a re-render does not repeat the warning.
const reportedMissing = new Set();

const readDictionary = (dictionary, key) => {
  if (!dictionary || !key) {
    return undefined;
  }
  if (typeof dictionary === "function") {
    return dictionary(key);
  }
  return dictionary[key];
};

/**
 * Look up the help text for a field.
 * The module dictionary wins; commonFieldTooltips is the fallback.
 */
export const resolveFieldTooltip = (dictionary, key, useCommon = true) => {
  const tooltip = readDictionary(dictionary, key);
  if (tooltip) {
    return tooltip;
  }
  return useCommon ? commonFieldTooltips[key] : undefined;
};

/**
 * Build the `fieldLabel` helper a form uses for every Form.Item label.
 *
 *   const fieldLabel = makeFieldLabel(leaveTypeTooltips, {
 *     namespace: "leaveType",
 *     label: (key) => trans(`${langKey}.${key}`),
 *   });
 *
 *   <Form.Item name="carry_forward" label={ fieldLabel("carry_forward") } />
 *   <Form.Item name="name" label={ fieldLabel("name", trans(Commons.name)) } />
 *
 * @param dictionary  Module help text: a plain object keyed by field, or a getter function.
 * @param options.label      Fallback label resolver used when the call site passes no label.
 * @param options.placement  Tooltip placement passed to FieldLabel (default "top").
 * @param options.maxWidth   Widest the tooltip bubble may grow before wrapping (default 360px).
 * @param options.namespace  Form name used in the development warning for missing keys.
 * @param options.common     Set false to opt out of the commonFieldTooltips fallback.
 */
export const makeFieldLabel = (dictionary, options = {}) => {
  const { label: labelResolver, placement = "top", namespace = "", common = true, maxWidth } = options;

  return (key, label) => {
    const text = label !== undefined ? label : (labelResolver ? labelResolver(key) : key);
    const tooltip = resolveFieldTooltip(dictionary, key, common);

    if (!tooltip && process.env.NODE_ENV === "development") {
      const reportKey = `${namespace}.${key}`;
      if (!reportedMissing.has(reportKey)) {
        reportedMissing.add(reportKey);
        console.warn(`[fieldTooltips] No help text for "${reportKey}". Add it to the module tooltip file.`);
      }
    }

    return <FieldLabel label={ text } tooltip={ tooltip } placement={ placement } maxWidth={ maxWidth } />;
  };
};

export default makeFieldLabel;
